/* ==========================================================================
   GAUI — product.js
   PDP behaviour: gallery swapping, variant selection (price / availability /
   URL / gallery + live "selected color name" label), quantity stepper, and the
   sticky mobile add-to-cart. Add-to-cart itself is handled by cart.js (the form
   submit is intercepted globally and opens the drawer).
   ========================================================================== */
(function () {
  "use strict";

  /* ----------------------------------------------------- <product-gallery> */
  if ("customElements" in window && !customElements.get("product-gallery")) {
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector("[data-pd-viewport]");
        this.addEventListener("click", (e) => {
          var thumb = e.target.closest("[data-thumb]");
          if (thumb) this.setActive(thumb.getAttribute("data-thumb"));
        });
        // Mobile swipe rail: keep the progress bars in step with the scroll.
        if (this.viewport) {
          this.viewport.addEventListener(
            "scroll",
            () => {
              if (this._raf) return;
              this._raf = requestAnimationFrame(() => {
                this._raf = null;
                if (this.viewport.scrollWidth <= this.viewport.clientWidth) return;
                var idx = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
                var slides = this.querySelectorAll("[data-media-id]");
                var slide = slides[Math.min(idx, slides.length - 1)];
                if (slide) this.markActive(slide.getAttribute("data-media-id"));
              });
            },
            { passive: true }
          );
        }
      }
      // Class bookkeeping only (desktop shows the active slide via CSS).
      markActive(mediaId) {
        mediaId = String(mediaId);
        this.querySelectorAll("[data-media-id]").forEach((m) => {
          m.classList.toggle("is-active", m.getAttribute("data-media-id") === mediaId);
        });
        this.querySelectorAll("[data-thumb]").forEach((t) => {
          t.classList.toggle("is-active", t.getAttribute("data-thumb") === mediaId);
        });
      }
      // Thumb / bar tap (and variant switch): mark it and, when the rail is
      // scrollable (mobile), glide to that slide.
      setActive(mediaId) {
        this.markActive(mediaId);
        if (this.viewport && this.viewport.scrollWidth > this.viewport.clientWidth) {
          var slide = this.querySelector('[data-media-id="' + String(mediaId) + '"]');
          if (slide) this.viewport.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
        }
      }
    }
    customElements.define("product-gallery", ProductGallery);
  }

  /* ------------------------------------------------- rich desc read-more --
     The excerpt keeps the merchant's formatting (bold FINAL SALE lines etc.)
     clamped to ~3 lines; the button expands/collapses it in place. */
  (function () {
    var moreBtn = document.querySelector("[data-pd-more-btn]");
    var descEl = document.querySelector("[data-pd-desc]");
    if (!moreBtn || !descEl) return;
    moreBtn.addEventListener("click", function () {
      var open = descEl.classList.toggle("is-open");
      moreBtn.textContent = open ? "Read less" : "Read more";
      moreBtn.setAttribute("aria-expanded", open ? "true" : "false");
    });
  })();

  /* ---------------------------------------------------------- reviews ----- */
  (function () {
    // Reviews accordion: Yotpo initializes hidden widgets lazily — nudge it
    // when the dropdown first opens so the review list always renders.
    var revAcc = document.querySelector("[data-pd-reviews]");
    if (revAcc) {
      revAcc.addEventListener("toggle", function () {
        if (revAcc.open && window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.initWidgets) {
          window.yotpoWidgetsContainer.initWidgets();
        }
      });
    }
  })();

  /* -------------------------------------------- colorway switch prefetch --
     The color dots navigate to sibling products. Prerender (Chromium) or
     prefetch-on-intent (everything else) makes that switch feel instant. */
  (function () {
    var dots = document.querySelectorAll("a.pd__cw-dot");
    if (!dots.length) return;
    if (window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports("speculationrules")) {
      var spec = document.createElement("script");
      spec.type = "speculationrules";
      spec.textContent = JSON.stringify({
        prerender: [{ urls: Array.prototype.map.call(dots, function (a) { return a.getAttribute("href"); }), eagerness: "moderate" }],
      });
      document.head.appendChild(spec);
    } else {
      dots.forEach(function (a) {
        var warm = function () {
          var l = document.createElement("link");
          l.rel = "prefetch";
          l.href = a.href;
          document.head.appendChild(l);
        };
        a.addEventListener("pointerenter", warm, { once: true });
        a.addEventListener("touchstart", warm, { once: true, passive: true });
      });
    }
  })();

  /* ---------------------------------------------------- <variant-selector> */
  if ("customElements" in window && !customElements.get("variant-selector")) {
    class VariantSelector extends HTMLElement {
      connectedCallback() {
        this.variants = this.parseJSON("[data-variant-data]", []);
        this.prices = this.parseJSON("[data-variant-prices]", {});
        this.idInput = document.querySelector("[data-variant-id]");
        this.gallery = document.querySelector("product-gallery");
        this.strings = {
          add: this.dataset.add || "Add to cart",
          sold: this.dataset.sold || "Sold out",
          unavailable: this.dataset.unavailable || "Unavailable",
        };
        this.addEventListener("change", () => this.onChange());
      }

      parseJSON(selector, fallback) {
        try {
          return JSON.parse(this.querySelector(selector).textContent);
        } catch (e) {
          return fallback;
        }
      }

      selectedOptions() {
        var opts = [];
        this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          opts[parseInt(input.getAttribute("data-option-position"), 10) - 1] = input.value;
          // Live "selected color name" label
          var label = this.querySelector('[data-selected-for="' + input.getAttribute("data-option-position") + '"]');
          if (label) label.textContent = input.value;
        });
        return opts;
      }

      onChange() {
        var selected = this.selectedOptions();
        var match = this.variants.find(function (v) {
          return v.options.every(function (opt, i) {
            return opt === selected[i];
          });
        });
        if (!match) {
          this.setAtc(false, this.strings.unavailable);
          return;
        }
        this.update(match);
      }

      update(variant) {
        if (this.idInput) this.idInput.value = variant.id;

        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set("variant", variant.id);
          history.replaceState({}, "", url.toString());
        }

        if (this.gallery && variant.featured_media) {
          this.gallery.setActive(variant.featured_media.id);
        }

        var price = this.prices[variant.id] || {};
        document.querySelectorAll("[data-product-price]").forEach((el) => {
          el.innerHTML = this.priceHTML(price);
        });

        this.setAtc(variant.available, variant.available ? this.strings.add : this.strings.sold, price.price);
      }

      // client bug report: the button price vanished after switching variants
      // — setAtc used to blank it and never refill. It now carries the new
      // variant's price through.
      //
      // Both writes are innerHTML, not textContent (client 2026-08-08: the
      // button read "ADD TO CART <span class=money>$48.00</span>" after any
      // size/color tap). Two reasons they must be markup, not text:
      //   price — this store's Shopify money format is itself HTML
      //     ("<span class=money>${{amount}}</span>", a legacy of its old
      //     currency app), so `| money` output only renders as a price when
      //     inserted as markup. Same treatment the main price already gets
      //     in priceHTML/[data-product-price].
      //   label — the server-rendered label carries the responsive
      //     "Add to Bag" (desktop) / "Add to Cart" (mobile) span pair, which
      //     a text write flattened to one string, silently changing the
      //     desktop copy after a variant switch.
      // Both values are theme/shop-controlled (our data-add attribute and
      // Shopify's money filter) — never shopper input.
      setAtc(available, text, priceText) {
        document.querySelectorAll("[data-atc]").forEach((btn) => {
          btn.disabled = !available;
          var t = btn.querySelector("[data-atc-text]");
          var p = btn.querySelector("[data-atc-price]");
          if (t) t.innerHTML = text;
          if (p) p.innerHTML = available && priceText ? priceText : "";
        });
      }

      priceHTML(p) {
        if (!p || !p.price) return "";
        if (p.compare) {
          return '<span class="price price--sale"><span class="price__sale">' + p.price +
            '</span> <s class="price__compare text-muted">' + p.compare + "</s></span>";
        }
        return '<span class="price">' + p.price + "</span>";
      }
    }
    customElements.define("variant-selector", VariantSelector);
  }

  /* ------------------------------------------------------- Quantity ------- */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-pdp-qty]");
    if (!btn) return;
    var input = btn.parentElement.querySelector("[data-pdp-qty-input]");
    if (!input) return;
    var step = parseInt(btn.getAttribute("data-pdp-qty"), 10);
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
  });

  /* ----------------------------------------------------- Sticky mobile ATC */
  function initStickyAtc() {
    var sticky = document.querySelector("[data-sticky-atc]");
    var anchor = document.querySelector(".product-form__row");
    if (!sticky || !anchor || !("IntersectionObserver" in window)) return;
    sticky.hidden = false; // CSS transform keeps it off-screen until .is-visible
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var scrolledPast = entry.boundingClientRect.top < 0;
          sticky.classList.toggle("is-visible", !entry.isIntersecting && scrolledPast);
        });
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  }

  if (document.readyState !== "loading") initStickyAtc();
  else document.addEventListener("DOMContentLoaded", initStickyAtc);
})();
